/**
 * `?next=` - where a staff member lands after signing in (M2.7).
 *
 * The value arrives from a URL, which means it arrives from anyone who can get a
 * receptionist to click a link. An open redirect on the sign-in page of an app that
 * holds a clinic's patient records is worth a great deal to whoever finds one: the
 * staff member signs in on the real clinic host, and the real clinic host then sends
 * them somewhere else entirely.
 *
 * The rule is narrow and has no exceptions: the target must be a path on this app.
 * Anything else - an absolute URL, a protocol-relative `//evil.example`, a backslash
 * that some parsers read as a slash, a control character - resolves to the fallback.
 *
 * This is `apps/super/src/lib/session/next-path.ts` with the admin app's default, kept
 * as a copy rather than shared because it is a security check that must be readable
 * where it is used, and a package boundary around eight lines buys nothing
 * (Docs/SUPER_ADMIN_UI.md §2.2).
 */

/** Where a staff member goes when there is no usable `next`. */
export const DEFAULT_NEXT_PATH = '/';

export function safeNextPath(raw: unknown, fallback: string = DEFAULT_NEXT_PATH): string {
  if (typeof raw !== 'string') return fallback;

  const value = raw.trim();
  if (value === '') return fallback;

  // Must be a rooted path, and only one slash: `//host` is protocol-relative.
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;
  // `\` is a path separator to some URL parsers, so `/\evil.example` is an escape.
  if (value.includes('\\')) return fallback;
  // A control character anywhere is a smuggling attempt, never a real route.
  // eslint-disable-next-line no-control-regex
  if (/[ -]/.test(value)) return fallback;
  // Sending someone back to the screen they just left is a loop, not a destination.
  if (value === '/login' || value.startsWith('/login?')) return fallback;
  // Likewise the enrolment gate, which navigates on its own once enrolment completes.
  if (value === '/mfa/enroll' || value.startsWith('/mfa/enroll?')) return fallback;

  return value;
}

/** `/login?next=<here>` for the route the staff member was trying to reach. */
export function loginPathFor(pathname: string, search: string = ''): string {
  const target = safeNextPath(`${pathname}${search}`, DEFAULT_NEXT_PATH);
  return `/login?next=${encodeURIComponent(target)}`;
}

/**
 * Whether the browser may be sent to `raw` when a support session ends.
 *
 * A super admin who used "View as" arrives on this clinic host inside a derived session
 * (SECURITY.md §6.3). When they leave, the natural destination is the platform console
 * they came from - a **different origin** from this app. Any URL the API hands us for
 * that is trusted far more than a query parameter and is still checked rather than
 * assumed:
 *
 * - the protocol must match this page's (no downgrade, no `javascript:`);
 * - the host must be this origin, or a sibling under the same parent domain - which is
 *   exactly what the platform host and a tenant's admin host are to each other.
 *
 * Anything else is refused and the caller stays put, which is a worse experience and a
 * correct one. Same rule as the super admin console's, deliberately: the two ends of
 * one hop should not disagree about what a trusted hop is.
 */
export function isTrustedRedirect(
  raw: string,
  current: { protocol: string; host: string },
): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }

  if (url.protocol !== current.protocol) return false;
  if (url.host === current.host) return true;

  // `admin.heyallergy.test` and `bay-allergy-admin.heyallergy.test` share a parent; a
  // bare hostname with no parent (`localhost`) never matches anything but itself.
  const parent = (host: string): string | null => {
    const hostname = host.split(':')[0] ?? host;
    const labels = hostname.split('.');
    return labels.length < 3 ? null : labels.slice(1).join('.');
  };
  const here = parent(current.host);
  return here !== null && here === parent(url.host);
}

/**
 * The `redirectUrl` a response carries, when it carries one.
 *
 * `DELETE /platform/impersonation/current` answers `204` with **no body** today, so
 * this narrows `unknown` and returns null every time. It exists because the shape the
 * platform console already receives from `POST /platform/impersonation` is
 * `{ redirectUrl }`, and when the exit route grows the same field (see
 * `Docs/ADMIN_AUTH_UI.md` open items) the banner should follow it rather than needing
 * new plumbing to do so.
 */
export function redirectUrlOf(result: unknown): string | null {
  if (typeof result !== 'object' || result === null) return null;
  const value = (result as { redirectUrl?: unknown }).redirectUrl;
  return typeof value === 'string' && value !== '' ? value : null;
}
